import { loadSwiper } from './swiper-loader.js';

const PREDICTIVE_SEARCH_RESOURCE_LIMIT = 10;

if (!customElements.get('predictive-search')) {
    class PredictiveSearch extends HTMLElement {

        constructor() {
            super();
            this.input = null;
            this.form = null;
            this.productsContainer = null;
            this.textResultsContainer = null;
            this.suggestionsContainer = null;
            this.layout = null;
            this.status = null;
            this.cachedResults = {};
            this.abortController = new AbortController();
            this.searchTerm = '';
            this.debounceTimer = null;
            this.productsSwiper = null;

            this.handleInput = this.handleInput.bind(this);
            this.handleFocus = this.handleFocus.bind(this);
            this.handleFocusOut = this.handleFocusOut.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);
        }

        connectedCallback() {
            this.form = this.querySelector('form');
            this.input = this.form?.querySelector('input[type="search"]');
            this.productsContainer = this.querySelector('[data-predictive-products]');
            this.textResultsContainer = this.querySelector('[data-predictive-text-results]');
            this.suggestionsContainer = this.querySelector('[data-search-suggestions-container]');
            this.layout = this.querySelector('.header-search--layout');
            this.status = this.querySelector('.predictive-search-status');

            if (!this.input || !this.productsContainer || !this.textResultsContainer) return;

            this.input.addEventListener('input', this.handleInput);
            this.input.addEventListener('focus', this.handleFocus);
            this.addEventListener('focusout', this.handleFocusOut);
            this.input.addEventListener('keydown', this.handleKeydown);

            this.#updateLastResultsBlock();
        }

        disconnectedCallback() {
            this.input?.removeEventListener('input', this.handleInput);
            this.input?.removeEventListener('focus', this.handleFocus);
            this.removeEventListener('focusout', this.handleFocusOut);
            this.input?.removeEventListener('keydown', this.handleKeydown);
            clearTimeout(this.debounceTimer);
            this.abortController.abort();
            this.#destroyProductsSwiper();
        }

        handleInput() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.#onChange(), 300);
        }

        handleFocus() {
            const query = this.#getQuery();

            if (!query.length) return;

            if (this.searchTerm !== query) {
                this.#onChange();
            } else if (this.#hasRenderedResults()) {
                this.#open();
            }
        }

        handleFocusOut() {
            setTimeout(() => {
                if (!this.contains(document.activeElement)) {
                    this.#close();
                }
            });
        }

        handleKeydown(event) {
            if (!this.#getQuery().length) {
                this.#close(true);
                return;
            }

            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                event.preventDefault();
                this.#switchOption(event.key === 'ArrowUp' ? 'up' : 'down');
            } else if (event.key === 'Enter') {
                const selected = this.querySelector('[aria-selected="true"] a');

                if (selected) {
                    event.preventDefault();
                    selected.click();
                }
            }
        }

        #onChange() {
            const newSearchTerm = this.#getQuery();

            if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
                this.#clearResults();
            }

            this.searchTerm = newSearchTerm;

            if (!this.searchTerm.length) {
                this.#resetView();
                this.#close(true);
                return;
            }

            this.layout?.classList.add('has-term');
            this.#getSearchResults(this.searchTerm);
        }

        #getQuery() {
            return this.input.value.trim();
        }

        #hasRenderedResults() {
            return Boolean(
                this.productsContainer.innerHTML.trim()
                || this.textResultsContainer.innerHTML.trim()
            );
        }

        #getSearchResults(searchTerm) {
            const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

            this.#setLoadingState();

            if (this.cachedResults[queryKey]) {
                this.#renderSearchResults(this.cachedResults[queryKey], searchTerm);
                return;
            }

            if (!window.routes?.predictive_search_url) return;

            this.abortController.abort();
            this.abortController = new AbortController();

            const params = new URLSearchParams({
                q: searchTerm,
                'resources[type]': 'product,collection,page,article',
                'resources[limit]': String(PREDICTIVE_SEARCH_RESOURCE_LIMIT),
                'resources[limit_scope]': 'each',
                section_id: 'predictive-search',
            });

            fetch(
                `${window.routes.predictive_search_url}?${params.toString()}`,
                { signal: this.abortController.signal }
            )
                .then((response) => {
                    if (!response.ok) {
                        this.#close();
                        throw new Error(response.status);
                    }

                    return response.text();
                })
                .then((text) => {
                    const results = this.#parseSectionResults(text);

                    this.cachedResults[queryKey] = results;
                    this.#renderSearchResults(results, searchTerm);
                })
                .catch((error) => {
                    if (error?.name === 'AbortError') return;
                    this.#close();
                });
        }

        #renderSearchResults(results, searchTerm) {
            const { products, collections, pages, articles } = results;
            const hasProducts = products.length > 0;
            const totalResults = products.length + collections.length + pages.length + articles.length;
            const hasAnyResults = totalResults > 0;

            this.#destroyProductsSwiper();

            if (hasProducts) {
                this.productsContainer.innerHTML = this.#buildProductsBlock(products, searchTerm);
                this.productsContainer.hidden = false;
                this.#initProductsSwiper();
            } else {
                this.productsContainer.innerHTML = '';
                this.productsContainer.hidden = true;
            }

            if (hasAnyResults) {
                this.removeAttribute('empty');

                const textResultsMarkup = this.#buildTextResults(collections, pages, articles, searchTerm);

                if (textResultsMarkup) {
                    this.textResultsContainer.innerHTML = textResultsMarkup;
                    this.textResultsContainer.hidden = false;
                } else {
                    this.textResultsContainer.innerHTML = '';
                    this.textResultsContainer.hidden = true;
                }
            } else {
                this.setAttribute('empty', '');
                this.textResultsContainer.innerHTML = this.#buildEmptyState(searchTerm);
                this.textResultsContainer.hidden = false;
            }

            this.#updateSuggestionsVisibility(hasProducts);
            this.#updateLastResultsBlock();
            this.#setResultsState(totalResults, searchTerm);
            window.initLazyLoad?.();
            this.#open();
        }

        #buildEmptyState(searchTerm) {
            const message = (this.dataset.labelNoResults || 'Your search for "{{ terms }}" didn\'t return any results. Please try again or browse our collections.')
                .replace('{{ terms }}', this.#escapeHtml(searchTerm));

            return `<p class="predictive-search--empty body">${message}</p>`;
        }

        #buildTextResults(collections, pages, articles, searchTerm) {
            const collectionsMarkup = this.#buildCollectionsBlock(collections, searchTerm);
            const pagesMarkup = this.#buildPagesBlock(pages, searchTerm);
            const articlesMarkup = this.#buildArticlesBlock(articles, searchTerm);

            if (!collectionsMarkup && !pagesMarkup && !articlesMarkup) return '';

            return `
                <div class="predictive-search--text-columns">
                    ${collectionsMarkup}
                    ${pagesMarkup}
                    ${articlesMarkup}
                </div>
            `;
        }

        #buildProductsBlock(products, searchTerm) {
            const heading = this.#formatLabel(
                this.dataset.labelProductsHeading,
                products.length
            );
            const viewAllUrl = this.#buildSearchUrl(searchTerm, 'product');
            const slides = products.map((product, index) => this.#buildProductSlide(product, index)).join('');

            return `
                <div class="predictive-search--block predictive-search--block-products">
                    <div class="predictive-search--block-header">
                        <p class="body predictive-search--block-heading">${heading}</p>
                        <div class="predictive-search--block-nav">
                            <button
                                type="button"
                                class="btn--arrow prev"
                                aria-label="${this.#escapeHtml(this.dataset.labelProductsPrev || 'Previous product')}"
                                data-predictive-products-nav="prev"
                            ></button>
                            <button
                                type="button"
                                class="btn--arrow next"
                                aria-label="${this.#escapeHtml(this.dataset.labelProductsNext || 'Next product')}"
                                data-predictive-products-nav="next"
                            ></button>
                        </div>
                    </div>
                    <div class="predictive-search--products-carousel swiper" data-predictive-products-carousel>
                        <div class="swiper-wrapper" role="listbox" aria-label="${this.#escapeHtml(heading)}">
                            ${slides}
                        </div>
                    </div>
                    <a href="${viewAllUrl}" class="predictive-search--view-all body">
                        ${this.#escapeHtml(this.dataset.labelViewAllProducts || 'View all product results')}
                    </a>
                </div>
            `;
        }

        #parseSectionResults(text) {
            const empty = {
                products: [],
                collections: [],
                pages: [],
                articles: [],
            };
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const jsonEl = doc.querySelector('[data-predictive-search-results]');

            if (!jsonEl) return empty;

            try {
                const parsed = JSON.parse(jsonEl.textContent);

                return {
                    products: parsed.products ?? [],
                    collections: parsed.collections ?? [],
                    pages: parsed.pages ?? [],
                    articles: parsed.articles ?? [],
                };
            } catch {
                return empty;
            }
        }

        #buildProductSlide(product, index) {
            const title = this.#escapeHtml(product.title);
            const url = this.#escapeHtml(product.url);
            const price = Number(product.price) || 0;
            const compareAtPrice = Number(product.compare_at_price) || 0;
            const hasCompareAt = compareAtPrice > price;
            const imageUrl = product.featured_image?.url || product.image;
            const imageAlt = this.#escapeHtml(product.featured_image?.alt || product.title);

            const imageMarkup = imageUrl
                ? `<img
                        src="${this.#escapeHtml(imageUrl)}"
                        alt="${imageAlt}"
                        width="960"
                        height="1280"
                        loading="eager"
                        class="cover in-view"
                    >`
                : '';

            return `
                <div
                    id="predictive-search-option-product-${index + 1}"
                    class="swiper-slide predictive-search--product predictive-search--item"
                    role="option"
                    aria-selected="false"
                >
                    <div class="predictive-search--product-info">
                        <div class="predictive-search--product-info-top">
                            <h3 class="predictive-search--product-title">${title}</h3>
                            <div class="predictive-search--product-price">
                                <s${hasCompareAt ? '' : ' hidden'}>${this.#formatMoney(compareAtPrice)}</s>
                                <span>${this.#formatMoney(price)}</span>
                            </div>
                        </div>
                    </div>
                    <div class="predictive-search--product-image overlay-link--parent">
                        <div class="predictive-search--product-image-wrapper">
                            ${imageMarkup}
                        </div>
                        <a href="${url}" class="overlay-link" tabindex="-1">
                            View more information about ${title}
                        </a>
                    </div>
                </div>
            `;
        }

        #buildCollectionsBlock(collections, searchTerm) {
            if (!collections.length) return '';

            const heading = this.#formatLabel(
                this.dataset.labelCollectionsHeading,
                collections.length
            );
            const items = collections.map((collection, index) => {
                const title = this.#escapeHtml(collection.title);
                const url = this.#escapeHtml(collection.url);

                return `
                    <li
                        id="predictive-search-option-collection-${index + 1}"
                        class="body predictive-search--item"
                        role="option"
                        aria-selected="false"
                    >
                        <a href="${url}" tabindex="-1">${title}</a>
                    </li>
                `;
            }).join('');

            return `
                <div class="predictive-search--block predictive-search--block-collections">
                    <p class="body predictive-search--block-heading">${heading}</p>
                    <ul class="predictive-search--block-list" role="listbox">
                        ${items}
                    </ul>
                </div>
            `;
        }

        #buildPagesBlock(pages, searchTerm) {
            if (!pages.length) return '';

            const heading = this.#formatLabel(
                this.dataset.labelPagesHeading,
                pages.length
            );
            const viewAllUrl = this.#buildSearchUrl(searchTerm, 'page');
            const items = pages.map((page, index) => {
                const title = this.#escapeHtml(page.title);
                const url = this.#escapeHtml(page.url);

                return `
                    <li
                        id="predictive-search-option-page-${index + 1}"
                        class="body predictive-search--item"
                        role="option"
                        aria-selected="false"
                    >
                        <a href="${url}" tabindex="-1">${title}</a>
                    </li>
                `;
            }).join('');

            return `
                <div class="predictive-search--block predictive-search--block-pages">
                    <p class="body predictive-search--block-heading">${heading}</p>
                    <ul class="predictive-search--block-list" role="listbox">
                        ${items}
                    </ul>
                </div>
            `;
        }

        #buildArticlesBlock(articles, searchTerm) {
            if (!articles.length) return '';

            const heading = this.#formatLabel(
                this.dataset.labelArticlesHeading,
                articles.length
            );
            const viewAllUrl = this.#buildSearchUrl(searchTerm, 'article');
            const items = articles.map((article, index) => {
                const title = this.#escapeHtml(article.title);
                const url = this.#escapeHtml(article.url);
                const imageUrl = article.image;
                const imageAlt = this.#escapeHtml(article.title);

                const imageMarkup = imageUrl
                    ? `<div class="predictive-search--item-image">
                            <img
                                src="${this.#escapeHtml(imageUrl)}"
                                alt="${imageAlt}"
                                width="36"
                                height="36"
                                loading="eager"
                                class="cover in-view"
                            >
                        </div>`
                    : '';

                return `
                    <li
                        id="predictive-search-option-article-${index + 1}"
                        class="body predictive-search--item predictive-search--item-article"
                        role="option"
                        aria-selected="false"
                    >
                        <a href="${url}" class="predictive-search--item-link" tabindex="-1">
                            ${imageMarkup}
                            <span>${title}</span>
                        </a>
                    </li>
                `;
            }).join('');

            const viewAllMarkup = articles.length >= PREDICTIVE_SEARCH_RESOURCE_LIMIT
                ? `<a href="${viewAllUrl}" class="predictive-search--view-all body">
                        ${this.#escapeHtml(this.dataset.labelViewAllArticles || 'View all article results')}
                    </a>`
                : '';

            return `
                <div class="predictive-search--block predictive-search--block-articles">
                    <p class="body predictive-search--block-heading">${heading}</p>
                    <ul class="predictive-search--block-list" role="listbox">
                        ${items}
                    </ul>
                    ${viewAllMarkup}
                </div>
            `;
        }

        async #initProductsSwiper() {
            const carousel = this.productsContainer.querySelector('[data-predictive-products-carousel]');
            const block = carousel?.closest('.predictive-search--block-products');
            const slides = carousel?.querySelectorAll('.swiper-slide');
            const nav = block?.querySelector('.predictive-search--block-nav');
            const next = nav?.querySelector('[data-predictive-products-nav="next"]');
            const prev = nav?.querySelector('[data-predictive-products-nav="prev"]');

            if (!carousel || !slides?.length) return;

            const enabled = slides.length > 2;

            if (nav) {
                nav.toggleAttribute('hidden', !enabled);
            }

            const Swiper = await loadSwiper({ waitForLoad: false });
            this.productsSwiper = new Swiper(carousel, {
                enabled,
                loop: false,
                slidesPerView: 'auto',
                spaceBetween: 8,
                slidesOffsetBefore: 12,
                slidesOffsetAfter: 12,
                observer: true,
                observeParents: true,
                navigation: {
                    nextEl: next,
                    prevEl: prev,
                },
                a11y: {
                    enabled: true,
                    prevSlideMessage: this.dataset.labelProductsPrev || 'Previous product',
                    nextSlideMessage: this.dataset.labelProductsNext || 'Next product',
                },
                breakpoints: {
                    768: {
                        spaceBetween: 10,
                    },
                    1024: {
                        slidesOffsetBefore: 0,
                    },
                },
            });

            requestAnimationFrame(() => {
                this.productsSwiper?.update();
            });

            carousel.setAttribute('data-swiper-ready', '');
        }

        #destroyProductsSwiper() {
            if (this.productsSwiper) {
                this.productsSwiper.destroy(true, true);
                this.productsSwiper = null;
            }
        }

        #updateSuggestionsVisibility(hasProducts) {
            if (!this.suggestionsContainer) return;

            this.suggestionsContainer.toggleAttribute('hidden', hasProducts);
        }

        #updateLastResultsBlock() {
            const blocks = [
                this.productsContainer,
                this.textResultsContainer,
                this.suggestionsContainer,
            ].filter(Boolean);

            for (const block of blocks) {
                block.classList.remove('header-search--results-last');
            }

            const hasTerm = this.layout?.classList.contains('has-term');
            let lastBlock = null;

            if (hasTerm) {
                if (!this.textResultsContainer.hidden) {
                    lastBlock = this.textResultsContainer;
                } else if (!this.productsContainer.hidden) {
                    lastBlock = this.productsContainer;
                } else if (this.suggestionsContainer && !this.suggestionsContainer.hidden) {
                    lastBlock = this.suggestionsContainer;
                }
            }

            lastBlock?.classList.add('header-search--results-last');
        }

        #resetView() {
            this.layout?.classList.remove('has-term');
            this.#clearResults();
            this.#updateSuggestionsVisibility(false);
            this.#updateLastResultsBlock();
        }

        #clearResults() {
            this.#destroyProductsSwiper();
            this.removeAttribute('empty');
            this.productsContainer.innerHTML = '';
            this.productsContainer.hidden = true;
            this.textResultsContainer.innerHTML = '';
            this.textResultsContainer.hidden = true;
        }

        #buildSearchUrl(searchTerm, type) {
            const url = new URL(window.routes.search_url, window.location.origin);

            url.searchParams.set('q', searchTerm);
            url.searchParams.set('type', type);

            return `${url.pathname}${url.search}`;
        }

        #formatLabel(template, count) {
            return this.#escapeHtml((template || '').replace('{{ count }}', count));
        }

        #formatMoney(cents) {
            if (typeof window.formatMoney === 'function') {
                return window.formatMoney(cents);
            }

            const amount = (Number(cents) / 100).toFixed(0);

            return `$${amount}`;
        }

        #escapeHtml(value) {
            return String(value ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        }

        #setLoadingState() {
            if (!this.status) return;

            const loadingText = this.status.dataset.loadingText || 'Loading';

            this.status.setAttribute('aria-hidden', 'false');
            this.status.textContent = loadingText;
            this.setAttribute('loading', '');
        }

        #setResultsState(totalResults, searchTerm) {
            if (!this.status) return;

            this.removeAttribute('loading');

            if (totalResults === 0) {
                const noResults = (this.dataset.labelNoResults || 'Your search for "{{ terms }}" didn\'t return any results. Please try again or browse our collections.')
                    .replace('{{ terms }}', searchTerm);

                this.status.textContent = noResults;
            } else {
                const resultsCount = (this.dataset.labelResultsCount || '{{ count }} results found')
                    .replace('{{ count }}', totalResults);

                this.status.textContent = resultsCount;
            }

            this.status.setAttribute('aria-hidden', 'false');

            setTimeout(() => {
                this.status.setAttribute('aria-hidden', 'true');
            }, 1000);
        }

        #switchOption(direction) {
            const moveUp = direction === 'up';
            const selectedElement = this.querySelector('[aria-selected="true"]');
            const visibleElements = Array.from(
                this.querySelectorAll('.predictive-search--item')
            ).filter((element) => element.offsetParent !== null);

            if (visibleElements.length === 0) return;
            if (moveUp && !selectedElement) return;

            const selectedIndex = visibleElements.indexOf(selectedElement);
            let activeIndex = 0;

            if (!moveUp && selectedElement) {
                activeIndex = selectedIndex === visibleElements.length - 1 ? 0 : selectedIndex + 1;
            } else if (moveUp) {
                activeIndex = selectedIndex === 0 ? visibleElements.length - 1 : selectedIndex - 1;
            }

            if (activeIndex === selectedIndex) return;

            const activeElement = visibleElements[activeIndex];

            activeElement.setAttribute('aria-selected', 'true');
            selectedElement?.setAttribute('aria-selected', 'false');
            this.input.setAttribute('aria-activedescendant', activeElement.id);
        }

        #open() {
            this.input.setAttribute('aria-expanded', 'true');
        }

        #close(clearResults = false) {
            if (clearResults) {
                this.#resetView();
                this.searchTerm = '';
            }

            const selected = this.querySelector('[aria-selected="true"]');

            selected?.setAttribute('aria-selected', 'false');
            this.input.setAttribute('aria-activedescendant', '');
            this.input.setAttribute('aria-expanded', 'false');
            this.removeAttribute('loading');
        }
    }

    customElements.define('predictive-search', PredictiveSearch);
}
